import React from 'react'
import { rememberReturnTo, takeReturnTo, clearReturnTo } from '@/lib/returnTo'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Mail, ArrowRight, KeyRound } from 'lucide-react'
import { useAuth } from '@/lib/AuthContext'
import { useRoute } from '@/lib/RouteContext'
import { useCommunity } from '@/lib/CommunityContext'
import { sdk } from '@/lib/sdk'
import type { LoginIntent } from '@/lib/loginIntent'
import { authFailureText } from '@/lib/authFailure'
import { erasureBlockOf, type ErasureBlock } from '@/lib/erasure'
import { ErasureLoginNotice } from '@/components/features/privacy/ErasureLoginNotice'
import { loginConsentSeen, loginConsentVersion, markLoginConsentSeen, signupConsentOf, useConsentTexts } from '@/components/features/sms/smsShared'
import { TwoFactorLoginStep } from '@/components/features/twoFactor/TwoFactorLoginStep'
import type { TwoFactorChallenge } from '@/lib/sdk-official/resources/two-factor'

interface LoginDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onLoginSuccess?: () => void
  /** What to show on opening (see `lib/loginIntent`): a typed e-mail, a notice, the recovery mode. */
  intent?: LoginIntent | null
}

export const LoginDialog = ({ open, onOpenChange, onLoginSuccess, intent }: LoginDialogProps) => {
  const { t, i18n } = useTranslation('auth')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [success, setSuccess] = React.useState('')
  const [warning, setWarning] = React.useState('')
  /** SEC-173: the API raised the login's proof of work (failed attempts); solving can take seconds. */
  const [heavyCheck, setHeavyCheck] = React.useState(false)
  const [authMethod, setAuthMethod] = React.useState<'recovery' | 'password'>('password')
  /** F-199: the login answered that this account's erasure is scheduled. */
  const [erasure, setErasure] = React.useState<ErasureBlock | null>(null)
  /** F-233: the password was right and the account has 2FA: the code step. */
  const [challenge, setChallenge] = React.useState<TwoFactorChallenge | null>(null)
  /** The consent box's answer, kept for after the code step. */
  const consentAnswerRef = React.useRef(false)
  const { signIn } = useAuth()
  const { navigate } = useRoute()
  const { community } = useCommunity()
  /* F-142: one unchecked box for notices and news (SMS and WhatsApp) under
     "Entrar", once per community and text version; it never blocks the login. */
  const consentTexts = useConsentTexts(community?.id, i18n.language)
  const serviceVersion = loginConsentVersion(consentTexts)
  const [consentShown, setConsentShown] = React.useState(false)
  const [consentChecked, setConsentChecked] = React.useState(false)
  React.useEffect(() => {
    if (open) {
      setConsentShown(Boolean(serviceVersion) && !loginConsentSeen(community?.id, serviceVersion))
      setConsentChecked(false)
    }
  }, [open, community?.id, serviceVersion])

  /* Opening the dialog is the moment the destination is still known: the person
     is looking at the page they wanted. After the redirect it is gone. */
  React.useEffect(() => {
    if (open) rememberReturnTo()
  }, [open])

  React.useEffect(() => {
    if (!open) {
      setEmail('')
      setPassword('')
      setError('')
      setSuccess('')
      setWarning('')
      setAuthMethod('password')
      setErasure(null)
      setChallenge(null)
    }
  }, [open])

  React.useEffect(() => {
    setError('')
    setSuccess('')
  }, [authMethod])

  // Applied after the mode reset above, so the notice survives it.
  React.useEffect(() => {
    if (!open || !intent) return
    if (intent.mode) setAuthMethod(intent.mode)
    if (intent.email) setEmail(intent.email)
    if (intent.notice) window.setTimeout(() => setSuccess(intent.notice as string), 0)
    if (intent.warning) window.setTimeout(() => setWarning(intent.warning as string), 0)
  }, [open, intent])

  const signInWithPassword = async () => {
    const withConsent = consentShown
    consentAnswerRef.current = withConsent
    const pending = await signIn(email, password, (level) => setHeavyCheck(level > 1), withConsent && consentChecked && serviceVersion ? { smsConsent: { textVersion: serviceVersion } } : undefined)
    /* F-233: no session yet; the code step finishes the login. */
    if (pending) {
      setErasure(null)
      setChallenge(pending)
      return
    }
    finishLogin()
  }

  const finishLogin = () => {
    if (consentAnswerRef.current) markLoginConsentSeen(community?.id, serviceVersion)
    onOpenChange(false)

    if (onLoginSuccess) {
      /* Whoever passed a callback stays where they are — the dialog was
         opened over the page that needed the session, not to leave it. */
      clearReturnTo()
      onLoginSuccess()
    } else {
      navigate(takeReturnTo() ?? '/')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setHeavyCheck(false)
    setError('')
    setSuccess('')
    setWarning('')

    try {
      if (authMethod === 'password') {
        await signInWithPassword()
      } else {
        await sdk.auth.recoveryPassword(email)
        setSuccess(t('recoveryEmailSent'))
      }
    } catch (error) {
      const block = erasureBlockOf(error)
      if (block) setErasure(block)
      else setError(authFailureText(error, t) ?? (error as Error).message)
    } finally {
      setIsLoading(false)
      setHeavyCheck(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden bg-white/80 backdrop-blur-2xl border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="sr-only">
            {t('loginTitle', { name: community?.name || 'Memberfy' })}
          </DialogTitle>
        </DialogHeader>
        {challenge ? (
          <div className="p-8 sm:p-10">
            <TwoFactorLoginStep
              email={email}
              challenge={challenge}
              onDone={finishLogin}
              onBack={() => { setChallenge(null); setPassword('') }}
            />
          </div>
        ) : erasure ? (
          <div className="p-8 sm:p-10">
            <ErasureLoginNotice
              block={erasure}
              email={email}
              password={password}
              onBack={() => { setErasure(null); setPassword('') }}
              onCanceled={signInWithPassword}
            />
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div className="p-8 sm:p-10 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-purple-500/[0.02] via-white/20 to-transparent pointer-events-none" />
            <h2 className="text-2xl font-semibold mb-2 text-gray-800">
              {t('loginTitle', { name: community?.name || 'Memberfy' })}
            </h2>
            <p className="text-gray-600 text-base mb-8">
              {authMethod === 'recovery'
                ? t('loginMagicLinkDescription')
                : t('loginDescription')
              }
            </p>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    className="pl-10 h-12 bg-white/90 backdrop-blur-sm border-white/20 hover:border-white/30 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-gray-800 text-base transition-all duration-200 rounded-xl"
                    required
                  />
                </div>
                {authMethod === 'password' && (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('passwordPlaceholder')}
                      className="pl-10 h-12 bg-white/90 backdrop-blur-sm border-white/20 hover:border-white/30 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-gray-800 text-base transition-all duration-200 rounded-xl"
                      required={authMethod === 'password'}
                      minLength={6}
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  variant="primary"
                  className="w-full h-12 text-base rounded-xl"
                >
                  {isLoading ? (
                    heavyCheck ? t('guard.verifying') : t('sending')
                  ) : (
                    <>
                      {authMethod === 'recovery' ? t('sendMagicLink') : t('enter')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
                {consentShown && authMethod === 'password' && signupConsentOf(consentTexts) && (
                  <label className="flex items-start gap-2 pt-1 text-sm text-gray-600" data-testid="login-phone-consent">
                    <input
                      type="checkbox"
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      disabled={isLoading}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary"
                    />
                    <span>{signupConsentOf(consentTexts)?.text}</span>
                  </label>
                )}
              </div>
              
              {warning && (
                <div role="status" className="backdrop-blur-sm border rounded-xl p-4 bg-amber-50/60 border-amber-100/50">
                  <p className="text-base text-center font-light text-amber-800">{warning}</p>
                </div>
              )}

              {success && (
                <div className="backdrop-blur-sm border rounded-xl p-4 bg-green-50/60 border-green-100/50">
                  <p className="text-base text-center font-light text-green-600">{success}</p>
                </div>
              )}

              {error && (
                <div className="backdrop-blur-sm border rounded-xl p-4 bg-red-50/60 border-red-100/50">
                  <p className="text-base text-center font-light text-red-500">{error}</p>
                </div>
              )}
              
              <div className="text-center text-gray-600">
                <button
                  type="button"
                  onClick={() => setAuthMethod(authMethod === 'recovery' ? 'password' : 'recovery')}
                  className="text-primary hover:text-primary-600 font-medium"
                >
                  {authMethod === 'recovery'
                    ? t('usePassword')
                    : t('useMagicLink')
                  }
                </button>
              </div>

              {community?.signupEnabled && (
                <div className="text-center text-gray-600">
                  <p>
                    {t('noAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => {
                        onOpenChange(false)
                        document.querySelector<HTMLElement>('[data-signup-trigger]')?.click()
                      }}
                      className="text-primary hover:text-primary-600 font-medium"
                    >
                      {community.signupRequiresApproval ? t('requestAccess') : t('signUp')}
                    </button>
                  </p>
                </div>
              )}
            </div>
          </div>
        </form>
        )}
      </DialogContent>
    </Dialog>
  )
}